import { Fragment, useEffect, useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';

import { buttonVariants } from '~/local/components/button';
import { cn } from '~/styling';

const NOTICE_SECONDS = 5;

interface DispatchNoticeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
}

/** Dispatch notice shown before starting any custom puzzle flow (photo or map). */
export function DispatchNoticeDialog({ isOpen, onClose, onContinue }: DispatchNoticeDialogProps) {
  const [noticeSeconds, setNoticeSeconds] = useState(NOTICE_SECONDS);

  useEffect(() => {
    if (!isOpen) return;

    setNoticeSeconds(NOTICE_SECONDS);
    const interval = setInterval(() => {
      setNoticeSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 z-10 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>
        <div className="flex min-h-screen items-center justify-center p-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="bg-primary-2-10 relative z-20 inline-block w-full max-w-md transform overflow-hidden rounded-2xl p-6 text-left align-middle shadow-xl transition-all">
              <Dialog.Title className="font-heading-condensed text-primary-2-80 text-2xl leading-7 font-medium">
                Please allow up to 7 days for dispatch
              </Dialog.Title>
              <div className="mt-3 space-y-3">
                <p className="text-primary-2-80 text-sm leading-6">
                  Orders are currently taking{' '}
                  <span className="font-bold">up to 7 days to be dispatched</span> after purchase.
                </p>
                <p className="text-neutral-2-60 text-sm leading-6">
                  Your puzzle will be crafted with the same care &mdash; thank you for your patience
                  and understanding.
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  data-testid="dispatch-notice-continue"
                  disabled={noticeSeconds > 0}
                  onClick={onContinue}
                  className={cn(
                    buttonVariants({ className: 'h-fit' }),
                    noticeSeconds > 0 && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {noticeSeconds > 0 ? `Please read (${noticeSeconds}s)` : 'Continue'}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
